import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PlatformSwitchesService } from '../../../services/platform-switches.service';
import { BusinessPlatformSwitch, PLATFORM_SWITCHES, PlatformSwitchCopy } from '../../../models/platform-switches.model';
import { errorMessage } from '../../agents/agents-format';
import { utcInstant } from '../../ai/images/ai-images-format';

/** What staff choose for a business: follow the platform, or on or off whatever it says. */
export type FeatureChoice = 'follow' | 'on' | 'off';

export interface FeatureRow extends BusinessPlatformSwitch {
  name: string;
  covers: string;
  whenOn: string;
  whenOff: string;
}

const choiceOf = (override: boolean | null): FeatureChoice => (override === null ? 'follow' : override ? 'on' : 'off');

/**
 * Businesses → profile → "Features" (specs/agents spec-v1.7, owner decision 2026-10-31): Agents, Plus Intelligence and Micro apps for this
 * business. By default a business follows AI › Feature switches; its own setting wins, both ways, so a feature can be piloted with one
 * business while it is off for the rest, or kept from one business while it is on for the rest. A change asks first, and is audited
 * against the business by the account API.
 */
@Component({
  selector: 'app-business-features-panel',
  standalone: false,
  templateUrl: './features-panel.component.html',
  styleUrls: ['../intelligence-panel/intelligence-panel.component.css', './features-panel.component.css']
})
export class BusinessFeaturesPanelComponent implements OnChanges {
  @Input() businessId?: string;

  readonly utc = utcInstant;

  rows: FeatureRow[] = [];
  loading = false;
  saving = false;
  error = '';
  /** Set when the account API has no feature switches or does not answer; replaces the body. */
  unavailable = '';

  /** The change waiting for its confirmation. */
  pending: { row: FeatureRow; choice: FeatureChoice } | null = null;

  constructor(private switches: PlatformSwitchesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) { this.load(); }
  }

  load(): void {
    if (!this.businessId) { return; }
    this.loading = true;
    this.error = '';
    this.unavailable = '';
    this.pending = null;
    this.switches.listForBusiness(this.businessId).subscribe({
      next: stored => {
        this.loading = false;
        this.rows = PLATFORM_SWITCHES.flatMap(copy => {
          const row = stored.find(s => s.key === copy.key);
          return row ? [this.toRow(copy, row)] : [];
        });
      },
      error: err => {
        this.loading = false;
        if (err?.status === 404 || err?.status === 0) {
          this.unavailable = 'The feature switches are not available right now.';
        } else {
          this.error = errorMessage(err, 'Could not load the feature switches.');
        }
      }
    });
  }

  choiceOf(row: FeatureRow): FeatureChoice {
    return choiceOf(row.override);
  }

  /** The choice as staff read it, with what following means today. */
  label(row: FeatureRow, choice: FeatureChoice): string {
    if (choice === 'follow') { return `Follow Plus (${row.platformEnabled ? 'on' : 'off'})`; }
    return choice === 'on' ? 'On for this business' : 'Off for this business';
  }

  /** A choice in the list: nothing is sent until it is confirmed. */
  choose(row: FeatureRow, choice: FeatureChoice): void {
    this.error = '';
    this.pending = choice === this.choiceOf(row) ? null : { row, choice };
  }

  /** What the business would get after the pending change. */
  get pendingOn(): boolean {
    if (!this.pending) { return false; }
    return this.pending.choice === 'follow' ? this.pending.row.platformEnabled : this.pending.choice === 'on';
  }

  /** What the pending change does; nothing to warn about when the business stays as it is. */
  get pendingEffect(): string {
    if (!this.pending || this.pendingOn === this.pending.row.enabled) { return ''; }
    return this.pendingOn ? this.pending.row.whenOn : this.pending.row.whenOff;
  }

  cancel(): void {
    this.pending = null;
    // The list shows the choice just made; put it back on what is stored.
    this.rows = [...this.rows];
  }

  confirm(): void {
    if (!this.pending || !this.businessId) { return; }
    const { row, choice } = this.pending;
    this.saving = true;
    this.error = '';
    this.switches.setForBusiness(this.businessId, row.key, choice === 'follow' ? null : choice === 'on').subscribe({
      next: saved => {
        this.saving = false;
        this.pending = null;
        this.rows = this.rows.map(r => (r.key === saved.key ? this.toRow(r, saved) : r));
      },
      error: err => {
        this.saving = false;
        this.error = errorMessage(err, `Could not change ${row.name} for this business.`);
      }
    });
  }

  private toRow(copy: PlatformSwitchCopy, stored: BusinessPlatformSwitch): FeatureRow {
    return {
      key: copy.key,
      name: copy.name,
      covers: copy.covers,
      whenOn: copy.whenOn,
      whenOff: copy.whenOff,
      enabled: stored.enabled,
      platformEnabled: stored.platformEnabled,
      override: stored.override,
      updatedAt: stored.updatedAt,
      updatedBy: stored.updatedBy
    };
  }
}
